// Customer "New request" — the cart builder.
//
// Layout:
//   1. Picker row: Recipient + Merchant + Category (cart mode only).
//   2. Two-column body (single column on mobile):
//        Left  — grid of available merchant items with + buttons.
//        Right — running cart with line items, qty steppers, subtotal HTG + USD.
//   3. Submit posts to POST /requests?submit=1, then routes to the new request.
//
// For text-mode categories (tuition, mobile_topup) we'd swap the grid
// for a description textarea — Phase 1.4.5 followup.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ShoppingBag, Plus, Minus, Trash2, Loader2, Store, User as UserIcon,
  Tag, LayoutDashboard, Package, Users, CreditCard, History, Repeat, Settings, Gift, MapPin,
  ShoppingCart, X, Heart, Bookmark,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  catalog, recipients, merchantItems, storefront, requests, favorites,
  type MerchantItem, type StorefrontMerchant,
} from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { PortalShell, type NavSection } from '../../components/shared/PortalShell';
import { RECIPIENT_NAV } from '../_navs';
import { Card, EmptyState } from '../../components/shared/primitives';
import { MapView } from '../../components/shared/MapView';
import { iconFor } from '../../lib/icons';
import { formatHtgMinor, formatUsdFromHtgMinor, deliveryEtaLabel } from '../../lib/money';
import { loadJSON, saveJSON, removeKey } from '../../lib/draftStore';

interface CartDraft {
  cart: CartLine[];
  merchantId: number | '';
  recipientId: number | '';
  categorySlug: string;
  notes: string;
  savedAt: number;
}

const CUSTOMER_NAV: NavSection[] = [
  { items: [
    { id: 'dashboard',  label: 'Dashboard',     icon: LayoutDashboard, href: '/customer' },
    { id: 'requests',   label: 'Requests',      icon: Package,         href: '/customer/requests' },
    { id: 'packages',   label: 'Care packages', icon: Gift,            href: '/customer/packages' },
    { id: 'recipients', label: 'Recipients',    icon: Users,           href: '/customer/recipients' },
  ]},
  { title: 'Payments', items: [
    { id: 'methods',    label: 'Payment methods', icon: CreditCard, href: '/customer/payment-methods' },
    { id: 'history',    label: 'History',         icon: History,    href: '/customer/history' },
    { id: 'recurring',  label: 'Recurring',       icon: Repeat,     href: '/customer/recurring' },
  ]},
  { title: 'Account', items: [
    { id: 'settings',   label: 'Settings',        icon: Settings,   href: '/customer/settings' },
  ]},
];

interface CartLine {
  merchant_item_id: number;
  name: string;
  unit: string;
  unit_price_htg_minor: number;
  qty: number;
  system_icon: string | null;
}

export function NewRequestPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Role: a customer builds a request FOR a recipient (address-book pick);
  // a recipient builds one for THEMSELF (no pick — they are the target).
  const { user } = useAuth();
  const isRecipient = user?.role === 'recipient';
  const self = isRecipient ? (user?.recipient_profile ?? null) : null;
  const [params] = useSearchParams();
  const merchantParam = params.get('merchant');   // preselect from a store page
  const reorderParam = params.get('reorder');      // re-add a past order's items
  const draftParam = params.get('draft');          // continue a saved draft
  const loadId = reorderParam ?? draftParam;        // both load a request into the builder
  // Auto-saved cart so an interrupted request resumes on return (per-user, per-device).
  const draftKey = `lakaycart:cart:v1:${user?.id ?? 'anon'}`;

  // Lookups.
  const recipQ    = useQuery({ queryKey: ['me', 'recipients'], queryFn: recipients.list, enabled: !isRecipient });
  const catsQ     = useQuery({ queryKey: ['catalog', 'categories'], queryFn: catalog.categories });
  const reorderQ  = useQuery({ queryKey: ['load-request', loadId], queryFn: () => requests.get(Number(loadId)), enabled: !!loadId });
  const favQ      = useQuery({ queryKey: ['favorites'], queryFn: favorites.list });
  const favIds    = useMemo(() => new Set(favQ.data?.ids ?? []), [favQ.data]);

  // Selection state — hydrated from the saved draft.
  const [recipientId, setRecipientId] = useState<number | ''>(() => loadJSON<CartDraft>(draftKey)?.recipientId ?? '');
  const [merchantId,  setMerchantId]  = useState<number | ''>(() => loadJSON<CartDraft>(draftKey)?.merchantId ?? '');
  const [categorySlug, setCategorySlug] = useState<string>(() => loadJSON<CartDraft>(draftKey)?.categorySlug ?? 'groceries');
  const [cart, setCart] = useState<CartLine[]>(() => loadJSON<CartDraft>(draftKey)?.cart ?? []);
  const [notes, setNotes] = useState(() => loadJSON<CartDraft>(draftKey)?.notes ?? '');
  const [cartOpen, setCartOpen] = useState(false);   // Instacart-style header cart drawer
  const [restored, setRestored] = useState(() => (loadJSON<CartDraft>(draftKey)?.cart?.length ?? 0) > 0);
  const [favOnly, setFavOnly] = useState(false);     // show only favorited items
  const [sourceDraftId, setSourceDraftId] = useState<number | null>(null);  // draft being completed

  // The delivery target — the recipient's own profile, or the customer's pick.
  const targetRecipient: { full_name: string; city?: { name: string; latitude?: string | null; longitude?: string | null } | null } | null =
    isRecipient ? self : (recipQ.data?.find((r) => r.id === recipientId) ?? null);
  const effectiveRecipientId = isRecipient ? (self?.id ?? '') : recipientId;
  const targetFirstName = isRecipient ? 'you' : (targetRecipient?.full_name?.split(' ')[0] ?? null);
  const cityName = targetRecipient?.city?.name ?? null;

  // Rank merchants nearest-to-target using the target's city centroid.
  const cityLat = targetRecipient?.city?.latitude;
  const cityLng = targetRecipient?.city?.longitude;
  const nearestMode = !!(cityLat && cityLng);
  const merchQ = useQuery({
    queryKey: ['storefront', 'merchants', cityLat ?? null, cityLng ?? null],
    queryFn:  () => storefront.merchants(nearestMode ? { lat: Number(cityLat), lng: Number(cityLng) } : undefined),
  });

  // Default recipient (customers only) once their address book loads.
  useEffect(() => {
    if (!isRecipient && recipientId === '' && recipQ.data && recipQ.data.length > 0) setRecipientId(recipQ.data[0]!.id);
  }, [isRecipient, recipQ.data, recipientId]);
  // The nearest merchant is re-picked when merchantId is '' (the recipient
  // picker clears it on an explicit change — see onChange below — so restored
  // drafts / reorders / ?merchant= preselects survive mount untouched).
  useEffect(() => {
    if (merchantId === '' && merchQ.data && merchQ.data.length > 0) {
      const fromParam = merchantParam ? merchQ.data.find((m) => m.user_id === Number(merchantParam)) : null;
      setMerchantId(fromParam ? fromParam.user_id : merchQ.data[0]!.user_id);
    }
  }, [merchQ.data, merchantId, merchantParam]);

  // Auto-save the working cart (and clear it when emptied).
  useEffect(() => {
    if (cart.length === 0) { removeKey(draftKey); return; }
    saveJSON(draftKey, { cart, merchantId, recipientId, categorySlug, notes, savedAt: Date.now() });
  }, [draftKey, cart, merchantId, recipientId, categorySlug, notes]);

  // Load a past order (?reorder=) or a saved draft (?draft=) into the builder once.
  const seeded = useRef(false);
  useEffect(() => {
    const r = reorderQ.data;
    if (!r || seeded.current) return;
    seeded.current = true;
    if (draftParam) setSourceDraftId(r.id);   // completing this draft → delete it on submit
    if (r.preferred_merchant_id) setMerchantId(r.preferred_merchant_id);
    if (!isRecipient && r.recipient_id) setRecipientId(r.recipient_id);
    const lines: CartLine[] = (r.items ?? [])
      .filter((it) => it.merchant_item_id)
      .map((it) => ({
        merchant_item_id: it.merchant_item_id as number,
        name: it.name_snapshot,
        unit: '',
        unit_price_htg_minor: it.unit_price_htg_minor,
        qty: Math.max(1, Math.round(Number(it.quantity))),
        system_icon: null,
      }));
    if (lines.length) {
      setCart(lines);
      setRestored(false);
      toast.success(draftParam ? 'Draft loaded — finish and submit' : `Re-added ${lines.length} item${lines.length === 1 ? '' : 's'} from your order`);
    }
  }, [reorderQ.data, isRecipient, draftParam]);

  // Pull the chosen merchant's items. Favorites mode pulls all categories so
  // we can show only the favorited ones.
  const itemsQ = useQuery({
    queryKey: ['merchant', merchantId, 'items', favOnly ? 'fav' : categorySlug],
    queryFn:  () => merchantItems.browse(Number(merchantId), favOnly ? undefined : { category: categorySlug }),
    enabled:  merchantId !== '',
  });
  const displayedItems = useMemo(
    () => favOnly ? (itemsQ.data ?? []).filter((it) => favIds.has(it.id)) : (itemsQ.data ?? []),
    [itemsQ.data, favOnly, favIds],
  );

  // Map pins: recipient city (orange) + each located merchant (selected green).
  const mapMarkers = useMemo(() => {
    const ms = (merchQ.data ?? [])
      .filter((m) => m.latitude && m.longitude)
      .map((m) => ({
        lat: Number(m.latitude), lng: Number(m.longitude),
        label: `${m.business_name}${m.distance_km != null ? ` — ${Math.round(m.distance_km)} km` : ''}`,
        color: m.user_id === merchantId ? '#16a34a' : '#1858bf',
      }));
    if (cityLat && cityLng) ms.unshift({ lat: Number(cityLat), lng: Number(cityLng), label: `${isRecipient ? 'You' : 'Recipient'} · ${cityName ?? ''}`, color: '#d97706' });
    return ms;
  }, [merchQ.data, merchantId, cityLat, cityLng, cityName, isRecipient]);

  const cartTotal = useMemo(() => cart.reduce((a, l) => a + l.unit_price_htg_minor * l.qty, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((a, l) => a + l.qty, 0), [cart]);

  const setQty = (mi: MerchantItem, qty: number) => {
    setCart((prev) => {
      const idx = prev.findIndex((l) => l.merchant_item_id === mi.id);
      if (qty <= 0) {
        if (idx >= 0) return prev.filter((_, i) => i !== idx);
        return prev;
      }
      const line: CartLine = {
        merchant_item_id:     mi.id,
        name:                 mi.name,
        unit:                 mi.unit,
        unit_price_htg_minor: mi.price_htg_minor,
        qty,
        system_icon:          mi.system_icon,
      };
      if (idx >= 0) {
        const next = [...prev]; next[idx] = line; return next;
      }
      return [...prev, line];
    });
  };
  const qtyOf = (id: number) => cart.find((l) => l.merchant_item_id === id)?.qty ?? 0;

  const categoryId = useMemo(
    () => (catsQ.data ?? []).find((c) => c.slug === categorySlug)?.id,
    [catsQ.data, categorySlug],
  );

  const toggleFav = useMutation({
    mutationFn: (itemId: number) => favorites.toggle(itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['favorites'] }),
  });

  const cartPayload = () => ({
    recipient_id:          Number(effectiveRecipientId),
    category_id:           categoryId!,
    preferred_merchant_id: Number(merchantId),
    notes:                 notes.trim() || null,
    items: cart.map((l) => ({ merchant_item_id: l.merchant_item_id, quantity: l.qty })),
  });

  const saveDraft = useMutation({
    mutationFn: () => {
      if (!effectiveRecipientId || !merchantId || !categoryId || cart.length === 0) {
        throw new Error('Add a merchant, category, and at least one item before saving.');
      }
      return requests.create(cartPayload());   // no submit → stays a draft
    },
    onSuccess: () => {
      removeKey(draftKey);
      qc.invalidateQueries({ queryKey: ['me', 'requests'] });
      toast.success('Saved for later — find it under Requests');
      navigate('/customer/requests', { replace: true });
    },
    onError: (e: Error) => toast.error(e.message || 'Save failed'),
  });

  const submit = useMutation({
    mutationFn: () => {
      if (!effectiveRecipientId || !merchantId || !categoryId || cart.length === 0) {
        throw new Error(isRecipient
          ? 'Pick a merchant, a category, and at least one item.'
          : 'Pick a recipient, a merchant, a category, and at least one item.');
      }
      return requests.create(cartPayload(), { submit: true });
    },
    onSuccess: (req) => {
      removeKey(draftKey);   // submitted — drop the saved draft
      if (sourceDraftId) requests.remove(sourceDraftId).catch(() => {});   // completing a draft → delete it
      qc.invalidateQueries({ queryKey: ['me', 'requests'] });
      qc.invalidateQueries({ queryKey: ['recipient', 'requests'] });
      toast.success(`Request submitted — ${req.cart_item_count} items, ${formatHtgMinor(req.cart_subtotal_htg_minor)}`);
      navigate(isRecipient ? '/recipient' : '/customer', { replace: true });
    },
    onError: (e: Error) => toast.error(e.message || 'Submit failed'),
  });

  return (
    <PortalShell
      nav={isRecipient ? RECIPIENT_NAV : CUSTOMER_NAV}
      activeId={isRecipient ? 'home' : 'requests'}
      title="New request"
      subtitle={isRecipient
        ? 'Pick what you need from a merchant near you. Your family will see it and pay.'
        : 'Build a cart of items from a merchant. Your family member will see it instantly.'}
      actions={<CartButton count={cartCount} total={cartTotal} onClick={() => setCartOpen(true)} />}
    >
      <div className="space-y-5">
        {restored && cart.length > 0 && (
          <div className="rounded-lg bg-brand-50 border border-brand-100 px-3.5 py-2.5 flex items-center justify-between gap-3">
            <p className="text-[12.5px] text-slate-700 inline-flex items-center gap-1.5">
              <ShoppingCart className="w-3.5 h-3.5 text-brand-600" /> We saved your cart — {cartCount} item{cartCount === 1 ? '' : 's'} restored from last time.
            </p>
            <button onClick={() => { setCart([]); setNotes(''); removeKey(draftKey); setRestored(false); }} className="text-[12px] font-semibold text-slate-500 hover:text-slate-900 shrink-0">Clear</button>
          </div>
        )}

        {/* Step 1 — pickers */}
        <Card>
          <div className={`grid gap-3 ${isRecipient ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
            {isRecipient ? (
              <div className="sm:col-span-1 flex flex-col justify-center rounded-md bg-brand-50 border border-brand-100 px-3 py-2">
                <span className="text-[10.5px] uppercase tracking-wider font-semibold text-brand-700 inline-flex items-center gap-1"><UserIcon className="w-3 h-3" /> Delivering to</span>
                <span className="mt-0.5 text-[13px] font-semibold text-slate-900">You{cityName ? ` · ${cityName}` : ''}</span>
              </div>
            ) : (
            <PickerSelect
              icon={UserIcon} label="For"
              value={recipientId === '' ? '' : String(recipientId)}
              onChange={(v) => { setRecipientId(v === '' ? '' : Number(v)); setMerchantId(''); }}
              loading={recipQ.isLoading}
              options={(recipQ.data ?? []).map((r) => ({
                value: String(r.id),
                label: `${r.full_name} · ${r.city?.name ?? ''}`,
              }))}
              emptyLabel="No recipients yet"
            />
            )}
            <PickerSelect
              icon={Store} label={nearestMode ? 'Merchant · nearest first' : 'Merchant'}
              value={merchantId === '' ? '' : String(merchantId)}
              onChange={(v) => setMerchantId(v === '' ? '' : Number(v))}
              loading={merchQ.isLoading}
              options={(merchQ.data ?? []).map((m: StorefrontMerchant) => ({
                value: String(m.user_id),
                label: `${m.business_name} · ${m.city?.name ?? ''}${m.distance_km != null ? ` — ${Math.round(m.distance_km)} km` : ''}`,
              }))}
              emptyLabel="No approved merchants"
            />
            <PickerSelect
              icon={Tag} label="Category"
              value={categorySlug}
              onChange={(v) => setCategorySlug(v)}
              loading={catsQ.isLoading}
              options={(catsQ.data ?? []).filter((c) => c.mode === 'cart').map((c) => ({ value: c.slug, label: c.label }))}
              emptyLabel="—"
            />
          </div>
          {merchantId !== '' && (
            <div className="mt-2.5 flex items-center gap-4 flex-wrap">
              <Link to={`/${isRecipient ? 'recipient' : 'customer'}/merchants/${merchantId}`} className="text-[12px] font-semibold text-brand-600 hover:underline inline-flex items-center gap-1">
                <Store className="w-3.5 h-3.5" /> View {merchQ.data?.find((mm) => mm.user_id === merchantId)?.business_name ?? 'this'} store →
              </Link>
              <button
                type="button"
                onClick={() => setFavOnly((v) => !v)}
                className={`inline-flex items-center gap-1 text-[12px] font-semibold ${favOnly ? 'text-rose-600' : 'text-slate-500 hover:text-slate-900'}`}
              >
                <Heart className={`w-3.5 h-3.5 ${favOnly ? 'fill-rose-500 text-rose-500' : ''}`} /> {favOnly ? 'Showing favorites' : 'Favorites'}{favIds.size > 0 ? ` (${favIds.size})` : ''}
              </button>
            </div>
          )}
        </Card>

        {mapMarkers.length > 0 && (
          <Card padding="p-0" className="overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
              <p className="text-[12px] font-semibold text-slate-700 inline-flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5 text-slate-400" /> Merchants near {cityName ?? (isRecipient ? 'you' : 'the recipient')}
              </p>
              <span className="text-[10.5px] text-slate-400">🟠 recipient · 🟢 selected · 🔵 other</span>
            </div>
            <MapView height="260px" markers={mapMarkers} />
          </Card>
        )}

        {/* Full-width item grid — the cart lives in the header button + drawer. */}
        <ItemsGrid
          items={displayedItems}
          loading={itemsQ.isLoading}
          favOnly={favOnly}
          qtyOf={qtyOf}
          setQty={setQty}
          favIds={favIds}
          onToggleFav={(id) => toggleFav.mutate(id)}
        />
      </div>

      {/* Instacart-style slide-over cart (opened from the header button) */}
      <CartDrawer
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        cartTotal={cartTotal}
        recipientName={targetFirstName}
        merchant={merchQ.data?.find((m) => m.user_id === merchantId) ?? null}
        notes={notes}
        setNotes={setNotes}
        setQty={(id, qty) => setCart((prev) =>
          qty <= 0 ? prev.filter((l) => l.merchant_item_id !== id)
                   : prev.map((l) => l.merchant_item_id === id ? { ...l, qty } : l)
        )}
        onSubmit={() => submit.mutate()}
        submitting={submit.isPending}
        canSubmit={!!effectiveRecipientId && !!merchantId}
        onSaveDraft={() => saveDraft.mutate()}
        savingDraft={saveDraft.isPending}
        showSaveDraft={!isRecipient}
      />
    </PortalShell>
  );
}

// ─── Header cart button (Instacart-style) ────────────────────

function CartButton({ count, total, onClick }: { count: number; total: number; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="relative inline-flex items-center gap-2 pl-3 pr-3.5 py-1.5 rounded-full bg-slate-900 text-white text-[12.5px] font-semibold hover:bg-slate-800 transition-colors"
    >
      <span className="relative">
        <ShoppingCart className="w-4 h-4" />
        {count > 0 && (
          <span className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-brand-500 text-white text-[10px] font-bold leading-none">{count}</span>
        )}
      </span>
      {total > 0 ? <span className="font-mono">{formatHtgMinor(total, { withSymbol: false })}</span> : <span>Cart</span>}
    </button>
  );
}

// ─── Cart drawer (slide-over) ────────────────────────────────

function CartDrawer({
  open, onClose, cart, cartTotal, recipientName, merchant, notes, setNotes, setQty, onSubmit, submitting, canSubmit,
  onSaveDraft, savingDraft, showSaveDraft,
}: {
  open: boolean;
  onClose: () => void;
  cart: CartLine[];
  cartTotal: number;
  recipientName: string | null;
  merchant: StorefrontMerchant | null;
  notes: string;
  setNotes: (v: string) => void;
  setQty: (id: number, qty: number) => void;
  onSubmit: () => void;
  submitting: boolean;
  canSubmit: boolean;
  onSaveDraft: () => void;
  savingDraft: boolean;
  showSaveDraft: boolean;
}) {
  if (!open) return null;
  const eta = deliveryEtaLabel(merchant?.avg_fulfillment_hours);

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 h-14 border-b border-slate-100 shrink-0">
          <button onClick={onClose} className="p-1 -ml-1 text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
          <div className="flex-1 text-center">
            <p className="text-[14px] font-semibold text-slate-900">Your cart</p>
            {recipientName && <p className="text-[11px] text-slate-500 -mt-0.5">For {recipientName}</p>}
          </div>
          <span className="w-6 shrink-0" />
        </div>

        {/* Store + ETA */}
        {merchant && (
          <div className="px-4 py-3 border-b border-slate-100 shrink-0">
            <p className="text-[13px] font-semibold text-slate-900 inline-flex items-center gap-1.5"><Store className="w-3.5 h-3.5 text-slate-400" /> {merchant.business_name}</p>
            {eta && <p className="mt-0.5 text-[11.5px] font-medium text-emerald-700">⚡ Delivery {eta.label}</p>}
          </div>
        )}

        {/* Items */}
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {cart.length === 0 ? (
            <div className="px-4 py-20 text-center text-slate-400">
              <ShoppingCart className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-[13px]">Your cart is empty.</p>
              <p className="text-[12px]">Pick items to add them here.</p>
            </div>
          ) : cart.map((l) => <DrawerLine key={l.merchant_item_id} l={l} setQty={setQty} />)}
        </div>

        {/* Note + checkout */}
        {cart.length > 0 && (
          <div className="border-t border-slate-100 p-3 space-y-2.5 shrink-0">
            <input
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Note for the merchant (optional)"
              className="w-full px-3 py-2 text-[12.5px] bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30"
            />
            <button
              onClick={onSubmit}
              disabled={submitting || !canSubmit}
              className="w-full h-12 rounded-full bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white font-semibold flex items-center justify-between px-5"
            >
              <span className="inline-flex items-center gap-2">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Submit request</span>
              <span className="font-mono">{formatHtgMinor(cartTotal)}</span>
            </button>
            {showSaveDraft && (
              <button onClick={onSaveDraft} disabled={savingDraft || !canSubmit} className="w-full py-2 rounded-full border border-slate-200 text-slate-700 text-[12.5px] font-semibold hover:bg-slate-50 disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
                {savingDraft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bookmark className="w-3.5 h-3.5" />} Save for later
              </button>
            )}
            <p className="text-[10.5px] text-slate-400 text-center leading-relaxed">Merchant adds delivery + platform fee in the quote. You'll review the USD total before paying.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function DrawerLine({ l, setQty }: { l: CartLine; setQty: (id: number, qty: number) => void }) {
  const Icon = iconFor(l.system_icon);
  return (
    <div className="px-4 py-3 flex items-center gap-3">
      <span className="inline-flex w-11 h-11 items-center justify-center rounded-lg bg-gradient-to-br from-brand-50 to-amber-50/40 text-brand-600 shrink-0"><Icon className="w-5 h-5" /></span>
      <div className="flex-1 min-w-0">
        <p className="text-[12.5px] font-semibold text-slate-900 leading-tight line-clamp-2">{l.name}</p>
        <p className="text-[11px] text-slate-500 mt-0.5">{formatHtgMinor(l.unit_price_htg_minor)} · {l.unit}</p>
        <p className="text-[11px] font-mono font-semibold text-slate-900 mt-0.5">{formatHtgMinor(l.unit_price_htg_minor * l.qty)}</p>
      </div>
      <div className="flex items-center rounded-full border border-slate-200 shrink-0">
        <button onClick={() => setQty(l.merchant_item_id, l.qty - 1)} className="px-2.5 py-1.5 text-slate-500 hover:text-slate-900">
          {l.qty === 1 ? <Trash2 className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
        </button>
        <span className="w-6 text-center text-[12.5px] font-mono font-semibold text-slate-900">{l.qty}</span>
        <button onClick={() => setQty(l.merchant_item_id, l.qty + 1)} className="px-2.5 py-1.5 text-slate-500 hover:text-slate-900"><Plus className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

// ─── Item grid ───────────────────────────────────────────────

function ItemsGrid({
  items, loading, favOnly, qtyOf, setQty, favIds, onToggleFav,
}: {
  items: MerchantItem[];
  loading: boolean;
  favOnly: boolean;
  qtyOf: (id: number) => number;
  setQty: (mi: MerchantItem, qty: number) => void;
  favIds: Set<number>;
  onToggleFav: (id: number) => void;
}) {
  if (loading) return <Card><div className="text-center py-12 text-slate-400 text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div></Card>;
  if (items.length === 0) return <Card><EmptyState icon={favOnly ? Heart : ShoppingBag} title={favOnly ? 'No favorites here yet' : 'No items in this category'} body={favOnly ? 'Tap the heart on items to save them for quick re-add.' : 'Try a different category or merchant.'} /></Card>;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
      {items.map((it) => {
        const Icon = iconFor(it.system_icon);
        const qty = qtyOf(it.id);
        const faved = favIds.has(it.id);
        return (
          <div key={it.id} className="bg-white border border-slate-100 rounded-xl p-3 flex flex-col relative">
            <button
              type="button"
              onClick={() => onToggleFav(it.id)}
              aria-label={faved ? 'Remove favorite' : 'Add favorite'}
              className="absolute top-2 right-2 z-10 p-1 rounded-full bg-white/80 hover:bg-white"
            >
              <Heart className={`w-4 h-4 ${faved ? 'fill-rose-500 text-rose-500' : 'text-slate-300 hover:text-rose-400'}`} />
            </button>
            <div className="aspect-square rounded-lg bg-gradient-to-br from-brand-50 to-amber-50/40 flex items-center justify-center text-brand-600 mb-3">
              <Icon className="w-8 h-8" />
            </div>
            <p className="text-[13px] font-semibold text-slate-900 leading-tight line-clamp-2">{it.name}</p>
            <p className="text-[10.5px] text-slate-500 mt-0.5">{it.unit}</p>
            <div className="mt-2 flex items-baseline justify-between">
              <span className="text-[13px] font-mono font-semibold text-slate-900">{formatHtgMinor(it.price_htg_minor)}</span>
              <span className="text-[10px] text-slate-400 font-mono">≈ {formatUsdFromHtgMinor(it.price_htg_minor)}</span>
            </div>
            <div className="mt-3">
              {qty === 0 ? (
                <button
                  type="button"
                  onClick={() => setQty(it, 1)}
                  className="w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-brand-600 text-white text-[12px] font-semibold hover:bg-brand-700"
                >
                  <Plus className="w-3 h-3" /> Add
                </button>
              ) : (
                <div className="flex items-center justify-between rounded-md border border-slate-200">
                  <button type="button" onClick={() => setQty(it, qty - 1)} className="px-2 py-1.5 text-slate-500 hover:bg-slate-50 rounded-l-md">
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="text-[13px] font-mono font-semibold text-slate-900">{qty}</span>
                  <button type="button" onClick={() => setQty(it, qty + 1)} className="px-2 py-1.5 text-slate-500 hover:bg-slate-50 rounded-r-md">
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Small picker select ───────────────────────────────────

function PickerSelect({
  icon: Icon, label, value, onChange, options, loading, emptyLabel,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  loading?: boolean;
  emptyLabel: string;
}) {
  return (
    <label className="block">
      <span className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 inline-flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading || options.length === 0}
        className="mt-1 w-full px-3 py-2 text-[13px] bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-400 disabled:bg-slate-50 disabled:text-slate-400"
      >
        {options.length === 0 ? (
          <option value="">{emptyLabel}</option>
        ) : (
          options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)
        )}
      </select>
    </label>
  );
}
